/**
 * Relay API Service
 *
 * Service for managing Nebula Relays (bridge VMs between Nebula SaaS and Cloud Gateways).
 * Used by Resellers and Cloud Providers to manage relay infrastructure.
 */

import type { OwnershipTransferMarker } from '../utils/vmOwnership'
import { apiClient } from './api'

const BASE_URL = '/api/v1'

// ============================================
// Types
// ============================================

export type NebulaRelayStatus = 'pending' | 'connected' | 'disconnected' | 'error'

export type NebulaRelay = {
  id: string
  cluster_id: string
  name: string
  status: NebulaRelayStatus
  mgmt_network: string
  mgmt_ip: string
  mgmt_bridge: string
  proxmox_vmid?: number | null
  proxmox_node?: string | null
  last_seen_at?: string | null
  error_message?: string | null
  connected_gateways_count: number
  commands_executed_count: number
  last_command_at?: string | null
  created_at: string
  updated_at: string
  is_connected: boolean
  is_healthy: boolean
}

export type NebulaRelayWithToken = {
  relay_token: string
} & NebulaRelay

export type NebulaRelayCreate = {
  name: string
  mgmt_network?: string
  mgmt_ip?: string
  mgmt_bridge?: string
  proxmox_node?: string
}

export type NebulaRelayUpdate = {
  name?: string
  proxmox_vmid?: number
  proxmox_node?: string
  error_message?: string
}

export type NebulaRelayStatusResponse = {
  id: string
  name: string
  status: NebulaRelayStatus
  is_connected: boolean
  is_healthy: boolean
  last_seen_at?: string | null
  connected_gateways_count: number
  uptime_seconds?: number | null
}

export type NebulaRelayTokenResponse = {
  relay_id: string
  relay_token: string
  message: string
}

export type NebulaRelayListResponse = {
  items: NebulaRelay[]
  total: number
}

export type NebulaRelayConfig = {
  relay_id: string
  relay_token: string
  nebula_url: string
  cluster_id: string
  mgmt_interface: string
  ssh_key_path: string
  log_level: string
  log_path: string
  reconnect_delay: number
  heartbeat_interval: number
}

export type NebulaRelaySSHKey = {
  relay_id: string
  ssh_public_key: string
}

// ============================================
// Cluster Assignment Types (for Resellers)
// ============================================

export type AssignedCluster = {
  id: string
  name: string
  host: string
  type: string
  version?: string | null
  description?: string | null
  status: string
  node_count: number
  vm_count: number
  total_cpu: number
  total_memory_gb: number
  total_storage_gb: number
  used_cpu: number
  used_memory_gb: number
  used_storage_gb: number
  assignment_id: string
  assigned_at: string
  has_relay: boolean
  relay_status?: NebulaRelayStatus | null
}

export type AssignedClusterListResponse = {
  clusters: AssignedCluster[]
  total: number
}

// ============================================
// API Functions - Relay Management
// ============================================

/**
 * Get the relay for a specific cluster
 */
export async function getClusterRelay(clusterId: string): Promise<NebulaRelay> {
  return apiClient.get<NebulaRelay>(`${BASE_URL}/clusters/${clusterId}/relay`)
}

/**
 * Create a new relay for a cluster
 * Note: Returns the token - save it, it won't be shown again!
 */
export async function createClusterRelay(
  clusterId: string,
  data: NebulaRelayCreate,
): Promise<NebulaRelayWithToken> {
  return apiClient.post<NebulaRelayWithToken>(`${BASE_URL}/clusters/${clusterId}/relay`, data)
}

/**
 * Update an existing relay
 */
export async function updateClusterRelay(
  clusterId: string,
  data: NebulaRelayUpdate,
): Promise<NebulaRelay> {
  return apiClient.put<NebulaRelay>(`${BASE_URL}/clusters/${clusterId}/relay`, data)
}

/**
 * Delete a relay
 */
export async function deleteClusterRelay(clusterId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/clusters/${clusterId}/relay`)
}

/**
 * Regenerate the relay token
 * Note: Old token will be invalidated!
 */
export async function regenerateRelayToken(clusterId: string): Promise<NebulaRelayTokenResponse> {
  return apiClient.post<NebulaRelayTokenResponse>(
    `${BASE_URL}/clusters/${clusterId}/relay/regenerate-token`,
    {},
  )
}

/**
 * Get real-time relay status
 */
export async function getRelayStatus(clusterId: string): Promise<NebulaRelayStatusResponse> {
  return apiClient.get<NebulaRelayStatusResponse>(`${BASE_URL}/clusters/${clusterId}/relay/status`)
}

/**
 * Get relay configuration JSON
 */
export async function getRelayConfig(clusterId: string): Promise<NebulaRelayConfig> {
  return apiClient.get<NebulaRelayConfig>(`${BASE_URL}/clusters/${clusterId}/relay/config`)
}

/**
 * Get relay SSH public key
 */
export async function getRelaySSHKey(clusterId: string): Promise<NebulaRelaySSHKey> {
  return apiClient.get<NebulaRelaySSHKey>(`${BASE_URL}/clusters/${clusterId}/relay/ssh-key`)
}

/**
 * List all relays for the organization
 */
export async function listRelays(params?: {
  status?: NebulaRelayStatus
  limit?: number
  offset?: number
}): Promise<NebulaRelayListResponse> {
  const queryParams = new URLSearchParams()
  if (params?.status) queryParams.append('status_filter', params.status)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.offset) queryParams.append('offset', params.offset.toString())

  const query = queryParams.toString()
  const url = query ? `${BASE_URL}/relays?${query}` : `${BASE_URL}/relays`
  return apiClient.get<NebulaRelayListResponse>(url)
}

// ============================================
// API Functions - Cluster Assignment (Reseller)
// ============================================

/**
 * List clusters assigned to the current reseller organization
 */
export async function getMyAssignedClusters(params?: {
  limit?: number
  offset?: number
}): Promise<AssignedClusterListResponse> {
  const queryParams = new URLSearchParams()
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.offset) queryParams.append('offset', params.offset.toString())

  const query = queryParams.toString()
  const url = query ? `${BASE_URL}/my-clusters?${query}` : `${BASE_URL}/my-clusters`
  return apiClient.get<AssignedClusterListResponse>(url)
}

/**
 * Get details of a specific assigned cluster
 */
export async function getMyAssignedCluster(clusterId: string): Promise<AssignedCluster> {
  return apiClient.get<AssignedCluster>(`${BASE_URL}/my-clusters/${clusterId}`)
}

// ============================================
// Helper Functions
// ============================================

export function getRelayStatusColor(status: NebulaRelayStatus): string {
  const colors: Record<NebulaRelayStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    connected: 'bg-green-100 text-green-800',
    disconnected: 'bg-gray-100 text-gray-800',
    error: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

export function getRelayStatusLabel(status: NebulaRelayStatus): string {
  const labels: Record<NebulaRelayStatus, string> = {
    pending: 'Pendente',
    connected: 'Conectado',
    disconnected: 'Desconectado',
    error: 'Erro',
  }
  return labels[status] || status
}

export function getRelayStatusIcon(status: NebulaRelayStatus): string {
  const icons: Record<NebulaRelayStatus, string> = {
    pending: 'Clock',
    connected: 'CheckCircle',
    disconnected: 'XCircle',
    error: 'AlertCircle',
  }
  return icons[status] || 'HelpCircle'
}

export function formatLastSeen(lastSeenAt?: string | null): string {
  if (!lastSeenAt) return 'Nunca visto'

  const lastSeen = new Date(lastSeenAt)
  const now = new Date()
  const diffMs = now.getTime() - lastSeen.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return 'Agora mesmo'
  } else if (diffMinutes < 60) {
    return `${diffMinutes} min atrás`
  } else if (diffHours < 24) {
    return `${diffHours}h atrás`
  }
  return `${diffDays}d atrás`
}

export function formatUptime(seconds?: number | null): string {
  if (!seconds) return '-'

  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

export function isRelayHealthy(relay: NebulaRelay): boolean {
  return relay.is_connected && relay.is_healthy && relay.status === 'connected'
}

export function getRelayHealthColor(relay: NebulaRelay): string {
  if (isRelayHealthy(relay)) {
    return 'text-green-600'
  } else if (relay.status === 'pending') {
    return 'text-yellow-600'
  } else if (relay.status === 'error') {
    return 'text-red-600'
  }
  return 'text-gray-600'
}

// ============================================
// Cloud Gateway Types (Reseller View)
// ============================================

export type CloudGatewayStatus =
  | 'pending'
  | 'provisioning'
  | 'configuring'
  | 'running'
  | 'stopped'
  | 'error'
  | 'deleting'
  | 'rebuilding'

export type WANConfig = {
  interface: string
  dhcp: boolean
  ip?: string | null
  gateway?: string | null
  netmask?: string | null
  dns: string[]
}

export type LANConfig = {
  interface: string
  vnet_id?: string | null
  ip?: string | null
  netmask?: string | null
  cidr?: string | null
}

export type GatewayFeatures = {
  nat: boolean
  firewall: boolean
  vpn: boolean
  loadbalancer: boolean
  dhcp_server: boolean
  rdpgw: boolean
}

export type GatewaySpecs = {
  cpu_cores: number
  memory_mb: number
  storage_gb: number
}

export type CloudGateway = {
  id: string
  name: string
  organization_id: string
  cluster_id: string
  reseller_id?: string | null
  proxmox_vmid?: number | null
  proxmox_node?: string | null
  template_id?: string | null
  status: CloudGatewayStatus
  status_message?: string | null
  /** `direct` (gRPC) ou `relay` (SSH). Recriar só existe em `direct`: sem stream gRPC não
   *  há como reaplicar as regras de firewall na VM nova. */
  connection_mode?: 'direct' | 'relay' | null
  wan: WANConfig
  lan: LANConfig
  features: GatewayFeatures
  specs: GatewaySpecs
  agent_version?: string | null
  created_at: string
  updated_at?: string | null
  provisioned_at?: string | null
  created_by?: string | null
}

export type CloudGatewayListResponse = {
  gateways: CloudGateway[]
  total: number
}

// ============================================
// API Functions - Cloud Gateway (Reseller)
// ============================================

/**
 * List gateways for all customers of the current reseller
 */
export async function listCustomerGateways(params?: {
  status?: CloudGatewayStatus
  skip?: number
  limit?: number
}): Promise<CloudGatewayListResponse> {
  const queryParams = new URLSearchParams()
  if (params?.status) queryParams.append('status_filter', params.status)
  if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params?.limit) queryParams.append('limit', params.limit.toString())

  const query = queryParams.toString()
  const url = query ? `${BASE_URL}/reseller/gateways?${query}` : `${BASE_URL}/reseller/gateways`
  return apiClient.get<CloudGatewayListResponse>(url)
}

/**
 * Get gateway for a specific customer
 */
export async function getCustomerGateway(customerId: string): Promise<CloudGateway> {
  return apiClient.get<CloudGateway>(`${BASE_URL}/reseller/gateways/${customerId}`)
}

// ============================================
// Gateway Helper Functions
// ============================================

export function getGatewayStatusColor(status: CloudGatewayStatus): string {
  const colors: Record<CloudGatewayStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    provisioning: 'bg-blue-100 text-blue-800',
    configuring: 'bg-blue-100 text-blue-800',
    running: 'bg-green-100 text-green-800',
    stopped: 'bg-gray-100 text-gray-800',
    error: 'bg-red-100 text-red-800',
    deleting: 'bg-orange-100 text-orange-800',
    rebuilding: 'bg-blue-100 text-blue-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

export function getGatewayStatusLabel(status: CloudGatewayStatus): string {
  const labels: Record<CloudGatewayStatus, string> = {
    pending: 'Pendente',
    provisioning: 'Provisionando',
    configuring: 'Configurando',
    running: 'Executando',
    stopped: 'Parado',
    error: 'Erro',
    deleting: 'Excluindo',
    rebuilding: 'Recriando',
  }
  return labels[status] || status
}

export function getGatewayStatusIcon(status: CloudGatewayStatus): string {
  const icons: Record<CloudGatewayStatus, string> = {
    pending: 'Clock',
    provisioning: 'Loader2',
    configuring: 'Settings',
    running: 'CheckCircle',
    stopped: 'StopCircle',
    error: 'XCircle',
    deleting: 'Trash2',
    rebuilding: 'Loader2',
  }
  return icons[status] || 'HelpCircle'
}

// ============================================
// API Functions - Gateway Agent Update (Reseller)
// ============================================

export type GatewayUpdateResponse = {
  accepted: boolean
  request_id: string
  target_version: string
  gateway_id: string
}

/** Resposta das ações de gateway que disparam uma task (`CloudGatewayActionResponse`). */
export type GatewayActionResponse = {
  id: string
  action: string
  status: CloudGatewayStatus
  message: string
  task_id?: string
}

/**
 * Get the active gateway agent package version
 */
export async function getActiveGatewayPackageVersion(): Promise<{ version: string | null }> {
  return apiClient.get<{ version: string | null }>(
    `${BASE_URL}/reseller/gateways/active-package-version`,
  )
}

/**
 * Update features (NAT, Firewall, VPN, DHCP) on a customer's gateway
 */
export async function updateCustomerGatewayFeatures(
  customerId: string,
  features: {
    nat_enabled?: boolean
    firewall_enabled?: boolean
    vpn_enabled?: boolean
    dhcp_server_enabled?: boolean
    rdpgw_enabled?: boolean
  },
): Promise<CloudGateway> {
  return apiClient.put<CloudGateway>(
    `${BASE_URL}/reseller/gateways/${customerId}/features`,
    features,
  )
}

/**
 * Reboot a customer's gateway VM
 */
export async function rebootCustomerGateway(
  customerId: string,
): Promise<{ id: string; action: string; status: string; message: string; task_id?: string }> {
  return apiClient.post(`${BASE_URL}/reseller/gateways/${customerId}/reboot`)
}

/**
 * Trigger OTA update on a customer's gateway agent
 */
export async function updateCustomerGatewayAgent(
  customerId: string,
  versionId?: string,
): Promise<GatewayUpdateResponse> {
  return apiClient.post<GatewayUpdateResponse>(
    `${BASE_URL}/reseller/gateways/${customerId}/update-agent`,
    versionId ? { version_id: versionId } : {},
  )
}

/**
 * Rebuild a customer's gateway VM.
 *
 * Destrói e recria a VM. A configuração é preservada: rede, features, senha SSH e as
 * regras (firewall, port-forwards, rotas), reaplicadas quando o agent reconecta.
 *
 * `gatewayId` identifica qual VM recriar — um cliente pode ter mais de um gateway
 * (multi-VPC), e a API recusa a ação se o alvo for ambíguo. Sempre envie o gateway que
 * o usuário selecionou: é uma destruição de VM, não há alvo "padrão" aceitável.
 */
export async function rebuildCustomerGateway(
  customerId: string,
  gatewayId?: string,
): Promise<GatewayActionResponse> {
  const query = gatewayId ? `?gateway_id=${encodeURIComponent(gatewayId)}` : ''
  return apiClient.post<GatewayActionResponse>(
    `${BASE_URL}/reseller/gateways/${customerId}/rebuild${query}`,
  )
}

// ============================================
// Types - Reseller Instances
// ============================================

export type ResellerInstanceCustomer = {
  id: string
  name: string
}

export type ResellerInstanceProduct = {
  id: string
  name: string
  type: string
}

export type ResellerInstance = {
  instance_id: string
  instance_name: string
  status: string
  billing_status: string
  customer: ResellerInstanceCustomer
  product: ResellerInstanceProduct
  cpu_cores: number
  memory_gb: number
  storage_gb: number
  cluster_name: string | null
  proxmox_node: string | null
  created_at: string
  next_billing_date: string | null
  unbilled_days: number | null
  error_message: string | null
  error_at: string | null
  // A última transferência (#1000) que NÃO concluiu — `failed` com etapa/erro ou
  // `in_progress`; `null`/ausente quando não há nada pendente. Rótulo em
  // `utils/vmOwnership.ts` (`ownershipTransferMarkerLabel`).
  ownership_transfer?: OwnershipTransferMarker | null
}

export type ResellerInstanceListResponse = {
  instances: ResellerInstance[]
  total: number
  skip: number
  limit: number
  has_more: boolean
}

// ============================================
// API Functions - Reseller Instances
// ============================================

/**
 * List VM instances for all customers of the current reseller
 */
export async function listResellerInstances(params?: {
  status_filter?: string
  customer_id?: string
  skip?: number
  limit?: number
}): Promise<ResellerInstanceListResponse> {
  const queryParams = new URLSearchParams()
  if (params?.status_filter) queryParams.append('status_filter', params.status_filter)
  if (params?.customer_id) queryParams.append('customer_id', params.customer_id)
  if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params?.limit) queryParams.append('limit', params.limit.toString())

  const query = queryParams.toString()
  const url = query ? `${BASE_URL}/reseller/instances?${query}` : `${BASE_URL}/reseller/instances`
  return apiClient.get<ResellerInstanceListResponse>(url)
}

// ============================================
// Export default object
// ============================================

export const relayApi = {
  // Relay management
  getClusterRelay,
  createClusterRelay,
  updateClusterRelay,
  deleteClusterRelay,
  regenerateRelayToken,
  getRelayStatus,
  getRelayConfig,
  getRelaySSHKey,
  listRelays,
  // Cluster assignment
  getMyAssignedClusters,
  getMyAssignedCluster,
  // Gateway management (reseller)
  listCustomerGateways,
  getCustomerGateway,
  getActiveGatewayPackageVersion,
  updateCustomerGatewayFeatures,
  rebootCustomerGateway,
  updateCustomerGatewayAgent,
  rebuildCustomerGateway,
  // Instance management (reseller)
  listResellerInstances,
}

export default relayApi
